import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { z } from 'zod';

import { AnimatedCard } from '@components/patterns';
import {
  Button,
  Flex,
  FormField,
  Input,
  Select,
  Text,
  useDialogContext,
} from '@components/primitives';

import { useTeamEnclaves } from '@components/features/Admin/queries';
import { api } from '@services/apiService';
import { adminTeamEnclaveLimits } from '@utils/resources/admin';

const ENCLAVE_TIERS = [
  { value: 'trial', label: 'Trial' },
  { value: 'pro', label: 'Pro' },
  { value: 'enterprise', label: 'Enterprise' },
];

const setTeamEnclaveLimitMutation = (teamUuid) => ({
  mutationFn: (enclaveLimitUpdates) =>
    api
      .post(adminTeamEnclaveLimits(teamUuid), {
        json: enclaveLimitUpdates,
      })
      .json(),
});

const updateTeamEnclaveLimitMutation = (teamUuid) => ({
  mutationFn: (enclaveLimitUpdates) =>
    api
      .put(adminTeamEnclaveLimits(teamUuid), {
        json: enclaveLimitUpdates,
      })
      .json(),
});

const enclaveLimitUpdateSchema = (maxInstances: number) =>
  z.object({
    maxInstances: z.number().int().min(maxInstances),
    tier: z
      .union([z.literal('trial'), z.literal('pro'), z.literal('enterprise')])
      .optional(),
    betaEndsTimestamp: z.string().optional(),
  });
type EnclaveLimitUpdateSchema = z.infer<
  ReturnType<typeof enclaveLimitUpdateSchema>
>;

export const EnclaveUsageLimitUpdate = ({ team, onSubmit }) => {
  const dialog = useDialogContext();
  const { data: teamEnclaveInfo } = useTeamEnclaves(team.uuid);

  const { mutateAsync: setTeamLimitUpdateAsync } = useMutation(
    setTeamEnclaveLimitMutation(team.uuid)
  );
  const { mutateAsync: updateTeamLimitUpdateAsync } = useMutation(
    updateTeamEnclaveLimitMutation(team.uuid)
  );

  const teamTotalActiveInstances =
    teamEnclaveInfo?.enclaves?.reduce(
      (acc, enclave) => acc + (enclave?.enclaveScalingConfig?.maxReplicas || 0),
      0
    ) || 0;
  const maxInstances = team?.enclaveUsageLimits?.maxInstances || 0;
  const currentTier = team?.enclaveUsageLimits?.tier || 'unset';
  const hasLimits = team?.enclaveUsageLimits != null;

  const {
    register,
    handleSubmit,
    control,
    formState: { isSubmitting, errors },
  } = useForm<EnclaveLimitUpdateSchema>({
    mode: 'onTouched',
    resolver: zodResolver(enclaveLimitUpdateSchema(teamTotalActiveInstances)),
    defaultValues: {
      maxInstances,
    },
  });

  const tier = useWatch({ control, name: 'tier' });

  const submissionHandler = async (values) => {
    const updates = {
      maxInstances: parseInt(values.maxInstances, 10),
      tier: values.tier,
      betaEndsTimestamp: values.betaEndsTimestamp
        ? new Date(values.betaEndsTimestamp).getTime()
        : undefined,
    };

    Object.keys(updates).forEach(
      (key) => updates[key] === undefined && delete updates[key]
    );

    if (Object.keys(updates).length === 0) {
      toast.error('No fields to update');
      return;
    }

    try {
      if (hasLimits) {
        await updateTeamLimitUpdateAsync(updates);
      } else {
        await setTeamLimitUpdateAsync(updates);
      }
      toast.success('Enclave limits updated');
      onSubmit();
      dialog.close();
    } catch {
      toast.error('Failed to update team limits');
    }
  };

  return (
    <>
      <Flex gap="20px" className="mb-4">
        <Flex.Item style={{ width: '100%' }} grow shrink>
          <AnimatedCard className="p-3">
            <Text.Heading size="sm">Active Instances</Text.Heading>
            <Text.Heading color="var(--primary)">
              {teamTotalActiveInstances}
            </Text.Heading>
          </AnimatedCard>
        </Flex.Item>

        <Flex.Item style={{ width: '100%' }} grow shrink>
          <AnimatedCard className="p-3">
            <Text.Heading size="sm">Max Instances</Text.Heading>
            <Text.Heading color="var(--primary)">{maxInstances}</Text.Heading>
          </AnimatedCard>
        </Flex.Item>
        <Flex.Item style={{ width: '100%' }} grow shrink>
          <AnimatedCard className="p-3">
            <Text.Heading size="sm">Enclaves Tier</Text.Heading>
            <Text.Heading color="var(--primary)">{currentTier}</Text.Heading>
          </AnimatedCard>
        </Flex.Item>
      </Flex>
      <form onSubmit={handleSubmit(submissionHandler)}>
        <FormField
          className="mb-4"
          label="Max Instances"
          helperText="The Max Enclave Instances this team can have"
          error={errors.maxInstances?.message}
        >
          <Input
            {...register('maxInstances', { valueAsNumber: true })}
            type="number"
          />
        </FormField>

        <Controller
          control={control}
          name="tier"
          render={({ field: { onChange, name, ref }, fieldState }) => (
            <FormField
              className="mb-4"
              label="Enclave Tier"
              error={fieldState.error?.message}
            >
              <Select
                placeholder="Select Tier"
                onChange={onChange}
                ref={ref}
                name={name}
                id="tier"
              >
                {ENCLAVE_TIERS.map((tier) => (
                  <Select.Option key={tier.value} value={tier.value}>
                    {tier.label}
                  </Select.Option>
                ))}
              </Select>
            </FormField>
          )}
        />

        {tier === 'trial' && (
          <FormField
            className="mb-4"
            label="Trial Expiration Date"
            helperText="The date and time this team's Enclave trial will expire."
            error={errors.betaEndsTimestamp?.message}
          >
            <Input type="datetime-local" {...register('betaEndsTimestamp')} />
          </FormField>
        )}

        <Button loading={isSubmitting}>
          {hasLimits ? 'Update' : 'Create'} team Enclave limits
        </Button>
      </form>
    </>
  );
};
